'use client';

import { usePathname } from 'next/navigation';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Library, LogOut, MoreHorizontal, Search, Settings2, UserRound, X } from 'lucide-react';
import { usePaletteShortcutHint } from '../../lib/shortcuts';
import { pinnedConversationComparator } from '../../lib/pinOrder';
import { useNavDragReorder, type DragRegion } from './useNavDragReorder';
import { productLine } from '../../lib/productVersion';
import { activeConversations, useBrainSessions, useHealth, useMe } from '../../lib/queries';
import { unreadConversationCount } from '../../lib/unreadConversations';
import { useSignOut } from '../../lib/mutations';
import { interpolate, useTranslation } from '../../lib/i18n';
import { renameConversation, setConversationPinned } from '../../lib/mutations/brain';
import { SkinSwitcher } from '../ui/SkinSwitcher';
import { Button } from '../ui/Button';
import { ShellLink } from './ShellLink';
import { useShellNavigation } from './useShellNavigation';
import { useNavCustomization } from './NavCustomization';
import { ContextMenuTrigger, type ContextMenuPoint, type MenuEntry } from '../ui/ContextMenu';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { navOrderIndex } from './navOrder';
import { entryIsActive, type NavEntry } from './navEntry';
import { sidebarLayout, subMenuPages, type SidebarGroupId } from './navGroups';
import { useSidebarRoute } from './useSidebarRoute';
import { useOpenSubMenus } from './useOpenSubMenus';
import { useSidebarResize } from './useSidebarResize';
import { ResizeHandle } from '../ui/ResizeHandle';
import { useMediaQuery } from '../../lib/useMobile';
import { COMMAND_PALETTE_OPEN_EVENT } from './CommandPalette';
import { Dialog, DialogContent, DialogOverlay } from '../ui/shadcn/dialog';
import { ToastSurface } from '../ui/toastSurface';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/shadcn/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/shadcn/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
} from '../ui/shadcn/sidebar';
import { useReturnFocus } from '../ui/overlayStack';
import { ConversationIconPicker } from '../ui/ConversationIconPicker';
import { openBrainSession } from '../../lib/brainDock';
import { useBrainChat } from '../../modules/advisor/BrainChatProvider';

/** The app's primary navigation: one column, built from the shadcn Sidebar primitive and arranged like
 *  the reference dashboard.
 *
 *  Top to bottom: a header naming the instance and opening its menu, the command palette's own row, the
 *  destinations in labelled groups with inline accordion sub-menus, the account alone at the end with air
 *  above it rather than a rule, and a footer holding the fold.
 *
 *  This component paints NOTHING. It states what each part IS (`sidebar-nav__*`) and what state it is in
 *  (`data-mode`, `data-open`, `data-active`, `data-ready`); every dimension, hairline and state colour
 *  lives in `app/styles/components/sidebar-nav.css`, driven by the `--sidebar-*` tokens. That split is
 *  what lets a skin retune the column by overriding tokens instead of out-specifying utility classes.
 *
 *  ICONS: lucide at 16px, `strokeWidth` 1.75, dimmed by the stylesheet — the set the whole app and every
 *  plugin already draw from (`lib/pluginIcons.ts` maps a plugin's declared icon name onto it). The
 *  reference uses Phosphor; adopting it here would mean a second icon vocabulary in the same column and
 *  plugin-declared icons that no longer resolve, which is a worse result than a 0.25px stroke difference.
 *  The spec permits either, and asks only that ONE set is used. */

/** Whether the sidebar's fold is toggled from the keyboard by this event.
 *
 *  `Ctrl`/`⌘` + `\` is deliberately not a browser binding — unlike `Ctrl+B` (Firefox bookmarks sidebar),
 *  `Ctrl+Shift+K` (Firefox console) or `⌘+.` (Safari stop) — and it does not collide with the command
 *  palette's `Ctrl/⌘+K`. The physical key is matched through `code` so a non-US layout, where the glyph
 *  sits elsewhere, still folds the sidebar with the key the label names. */
function isCollapseShortcut(event: KeyboardEvent): boolean {
  if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return false;
  return event.code === 'Backslash' || event.key === '\\';
}

/** The drawer mounts on open so the shared return-focus helper captures the actual hamburger. Radix owns
 *  modality, background aria isolation, Escape and the focus trap; this wrapper only supplies the opener
 *  because the shell has no `Dialog.Trigger` for Radix to remember. */
function SidebarNavSheet({ children, label, onClose, returnFocusTo }: { children: React.ReactElement; label: string; onClose?: () => void; returnFocusTo: HTMLElement | null }) {
  const { restoreFocus } = useReturnFocus();
  const restoreDrawerFocus = useCallback(() => {
    if (returnFocusTo?.isConnected && !returnFocusTo.inert && !returnFocusTo.closest('[inert]')) {
      returnFocusTo.focus({ preventScroll: true });
      return;
    }
    restoreFocus();
  }, [restoreFocus, returnFocusTo]);
  // The shell may close by changing the controlled prop directly (route arrival, close button), bypassing
  // Radix's close event. A layout cleanup runs before the surface leaves the DOM and restores only when
  // focus is still inside it; focus the user moved elsewhere remains theirs.
  useLayoutEffect(() => () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('[data-testid="sidebar-navigation"]')) restoreDrawerFocus();
  }, [restoreDrawerFocus]);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogOverlay presentation="sheet" layer="drawer" className="sidebar-nav__scrim" data-open>
        <DialogContent
          asChild
          presentation={null}
          aria-label={label}
          aria-labelledby={undefined}
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            restoreDrawerFocus();
          }}
        >
          {children}
        </DialogContent>
      </DialogOverlay>
    </Dialog>
  );
}

export function SidebarNav({ compact = false, measured = true, side = 'left', onToggleCollapse, drawer = false, drawerOpen = false, onDrawerClose, regionWidth = 0, scale = 1 }: {
  regionWidth?: number;
  scale?: number;
  compact?: boolean;
  measured?: boolean;
  side?: 'left' | 'right';
  onToggleCollapse?: () => void;
  drawer?: boolean;
  drawerOpen?: boolean;
  onDrawerClose?: () => void;
}) {
  const pathname = usePathname();
  const { t } = useTranslation();
  const health = useHealth();
  const { signOut, isPending: signingOut } = useSignOut();
  const me = useMe();
  const { worlds, allWorlds, layout, layoutReady } = useShellNavigation();

  // An untouched menu keeps the shared default sequence, which carries meaning of its own (where you
  // land, the work, what it runs on, administration). Once the user has arranged the menu, their order
  // is already in `worlds` and wins outright.
  const sequence = useMemo<NavEntry[]>(() => (layout.order.length === 0
    ? [...worlds].sort((a, b) => navOrderIndex(a.href) - navOrderIndex(b.href))
    : worlds), [worlds, layout.order.length]);
  const { groups } = useMemo(() => sidebarLayout(sequence), [sequence]);
  // THIS surface's own visible order, which is what the first edit seeds the stored order from. Handing
  // over the registry order instead would reshuffle the column the moment anything was hidden or moved.
  const displayOrder = useMemo(
    () => groups.flatMap((group) => group.entries).flatMap((entry) => (entry.id ? [entry.id] : [])),
    [groups],
  );
  const customization = useNavCustomization(allWorlds, layout, displayOrder);

  // Where the reader is, answered once for the whole column: the section that paints as the current
  // place, the one row that carries `aria-current`, and the sub-menu that must be open to show it.
  const route = useSidebarRoute(sequence);
  const userId = me.data?.user?.id ?? null;
  const subMenus = useOpenSubMenus(userId, drawer || !compact ? route.openId : undefined);
  const finePointer = useMediaQuery('(pointer: fine)');
  const resize = useSidebarResize({ layout, userId, expanded: !drawer && !compact && finePointer === true, regionWidth, scale, side });

  const sessions = useBrainSessions();
  const pinnedConversations = useMemo(
    () => activeConversations(sessions.data ?? []).filter(session => session.pinnedAt !== null)
      .sort(pinnedConversationComparator(layout.pinOrder)),
    [sessions.data, layout.pinOrder],
  );
  const pinnedIds = pinnedConversations.map(session => session.id);
  const regionEntries = useCallback(
    (region: DragRegion): readonly { id?: string }[] => region === 'pins'
      ? pinnedConversations : groups.find((group) => group.id === region)?.entries ?? [],
    [groups, pinnedConversations],
  );
  const dragReorder = useNavDragReorder({
    drawer,
    regionEntries,
    onReorder: (id, region, slot) => {
      if (region === 'pins') { customization.reorderPinTo(pinnedIds, id, slot); return; }
      // The drop names the entry whose seat it takes. A stored layout interleaves entries this column
      // presents in separate groups, so a slot in this surface's partition is no index into that order.
      const targetId = regionEntries(region)[slot]?.id;
      if (targetId) customization.reorderTo(id, targetId);
    },
  });

  // Arriving somewhere is the end of navigating, so the sheet gets out of the way on its own. The close
  // callback is an unstable inline prop from the shell — deliberately not a dependency.
  useEffect(() => { if (drawer) onDrawerClose?.(); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // The fold is a desktop affordance, so the shortcut exists exactly where the control does:
  // `onToggleCollapse` is absent in the sheet and in a window already forced to the icon column, where
  // it could change nothing.
  useEffect(() => {
    if (!onToggleCollapse) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (!isCollapseShortcut(event)) return;
      event.preventDefault();
      onToggleCollapse();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToggleCollapse]);

  const hidden = drawer && !drawerOpen;
  const drawerReturnFocusRef = useRef<HTMLElement | null>(null);
  const previousDrawerOpenRef = useRef(false);
  if (drawer && drawerOpen && !previousDrawerOpenRef.current && typeof document !== 'undefined') {
    drawerReturnFocusRef.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null;
  }
  previousDrawerOpenRef.current = drawerOpen;
  const mode = drawer ? 'drawer' : compact ? 'rail' : 'full';

  /** Live counts, keyed by the entry id they annotate. Zero is not a badge: a row that permanently wears
   *  a "0" is noise the reader learns to stop seeing.
   *
   *  The Chat count is the reader's OWN busy conversations, read from the conversation list the chat rail
   *  and the operation dock already hold under a SHARED react-query key, so the badge costs no request of
   *  its own and the owner-scoped `conversations` SSE moves all of them (see useElowenEvents). */
  const workingConversations = useMemo(
    () => activeConversations(sessions.data ?? []).filter((session) => session.programStatus.state === 'working' || session.backgroundWorking).length,
    [sessions.data],
  );
  const counters = useMemo<Record<string, { count: number; title: string; live?: boolean }>>(
    () => (workingConversations > 0
      ? { chat: { count: workingConversations, title: `${t.nav.workingConversations}: ${workingConversations}`, live: true } }
      : {} as Record<string, { count: number; title: string; live?: boolean }>),
    [workingConversations, t.nav.workingConversations],
  );
  // Pins share the history's owner-scoped listing; activity never reorders them.
  const { activeSessionId, openHistory, deleteSession } = useBrainChat();
  // The same count the browser tab title shows, from the same owner-scoped listing.
  const unreadConversations = unreadConversationCount(activeConversations(sessions.data ?? []));
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pinDelete, setPinDelete] = useState<{ id: string; title: string } | null>(null);

  /** Pinned conversation actions share the conversation mutation and cache owner. */
  const pinActions = (id: string, title: string, shown: string): MenuEntry[] => {
    return [
      { action: 'unpin', onClick: () => {
        void setConversationPinned(qc, id, false).catch(() => toast(t.chat.pinError, 'error'));
      } },
      { action: 'rename', edit: { value: title, onCommit: async (next) => {
        try { await renameConversation(qc, id, next); }
        catch { toast(t.chat.renameError, 'error'); return false; }
        return true;
      } } },
      { action: 'delete', onClick: () => setPinDelete({ id, title: shown }) },
    ];
  };
  // A mouse reorders the pins by dragging; where the drag does not run (the sheet, a touch screen) the
  // pin menu carries the arrows instead.
  const pinMoves = () => drawer || window.matchMedia('(pointer: coarse)').matches;
  const openPinMenu = (point: ContextMenuPoint, session: { id: string; title: string }) =>
    customization.openPinMenu(point, pinnedIds, session.id,
      pinActions(session.id, session.title, session.title || t.brainChat.untitled), pinMoves());
  const pinDeleteDialog = (
    <ConfirmDialog open={pinDelete !== null} title={t.brainChat.deleteChatConfirmTitle}
      description={pinDelete ? interpolate(t.brainChat.deleteChatConfirmDescription, { title: pinDelete.title }) : undefined}
      confirmLabel={t.brainChat.deleteChat}
      failureMessage={() => t.brainChat.deleteChatError}
      onConfirm={async () => {
        if (!pinDelete) return;
        await deleteSession(pinDelete.id);
        setPinDelete(null);
      }}
      onClose={() => setPinDelete(null)} />
  );

  const paletteHint = usePaletteShortcutHint();

  const entryMenu = (entry: NavEntry, region: SidebarGroupId) => (point: ContextMenuPoint) => (entry.id
    ? customization.openEntryMenu(point, entry, regionEntries(region))
    : customization.openSurfaceMenu(point));

  /** The row wrapper: shadcn's `<li>`, plus the drag contract. The shell owns MOVEMENT, the row inside
   *  owns its own paint. `--stagger` is the row's index, read by the entrance animation. */
  const entryShell = (entry: NavEntry, region: SidebarGroupId, slot: number, child: React.ReactNode) => {
    const dragProps = dragReorder.entryProps(entry, region, slot);
    return (
      // The whole row opens the entry's menu: its link or disclosure, its chevron and its pages.
      <ContextMenuTrigger onOpen={entryMenu(entry, region)}>
        <SidebarMenuItem
          {...dragProps}
          className="sidebar-nav__entry animate-rise-in"
          data-nav-entry-id={entry.id}
          style={{ ...dragProps.style, '--stagger': slot } as React.CSSProperties}
        >
          {child}
        </SidebarMenuItem>
      </ContextMenuTrigger>
    );
  };

  /** A destination row at the top level.
   *
   *  The label stays mounted so the collapse transition can finish, but is `aria-hidden` in the icon
   *  column; `aria-label` names the row exactly where the text is not presented.
   *
   *  ONE hover hint, the native `title`, in BOTH modes. Expanded it keeps a truncated label readable;
   *  folded it names the glyph.
   *
   *  It used to be the primitive's `tooltip` while folded — a styled panel beside the rail. That panel is
   *  not portalled (see ui/shadcn/tooltip.tsx) and leans on `position: fixed` to escape the column's
   *  `overflow: hidden`, but the row's entrance animation keeps `transform: translateY(0)` applied
   *  through its `both` fill, which makes the row a containing block and re-anchors the panel INSIDE the
   *  57px rail. The reader saw a sliver of a rounded panel sliding out beside the icon. Widening the
   *  clipping chain is not available either: the column scrolls, and `overflow-x: visible` beside
   *  `overflow-y: auto` computes back to `auto`. The rail is hint-only, exactly like its search field and
   *  its `…` control, both of which have always used the native `title`. */
  const destination = (entry: NavEntry) => {
    const active = entryIsActive(entry, pathname);
    const currentPage = active && entry.href !== undefined && route.currentHref === entry.href;
    const Icon = entry.icon;
    // A plugin's own count travels on the entry (see NavEntry.badge); the shell's live counters win
    // where both exist, because those are the ones a SSE signal keeps moving.
    const badge = (entry.id ? counters[entry.id] : undefined)
      ?? (entry.badge ? { count: entry.badge, title: String(entry.badge), live: false } : undefined);
    const hint = badge ? `${entry.label} · ${badge.title}` : entry.label;
    return (
      <SidebarMenuButton asChild isActive={active} className="sidebar-nav__item">
        <ShellLink
          href={entry.href ?? '#'}
          draggable={false}
          aria-current={currentPage ? 'page' : undefined}
          aria-label={compact ? entry.label : undefined}
          title={hint}
        >
          <span className="sidebar-nav__icon" aria-hidden><Icon size={16} strokeWidth={1.75} /></span>
          <span className="sidebar-nav__label" aria-hidden={compact || undefined}>{entry.label}</span>
          {badge ? <SidebarMenuBadge aria-hidden className="sidebar-nav__badge" data-live={badge.live || undefined}>{badge.count}</SidebarMenuBadge> : null}
        </ShellLink>
      </SidebarMenuButton>
    );
  };

  /** A row that discloses its pages inline instead of leading anywhere itself.
   *
   *  The parent is a BUTTON, not a link: every page of the group is a row inside it, including the one
   *  the entry's own `href` points at, so there is nothing left for the parent to navigate to. Radix
   *  `Collapsible` owns `aria-expanded`, the trigger/content wiring and the open/closed data attributes;
   *  the stylesheet animates the disclosure from them. */
  const subMenu = (entry: NavEntry, pages: NonNullable<NavEntry['subItems']>) => {
    const key = entry.id ?? entry.label;
    const active = entryIsActive(entry, pathname);
    const open = subMenus.isOpen(key);
    const Icon = entry.icon;
    return (
      <Collapsible open={open} onOpenChange={() => subMenus.toggle(key)} className="sidebar-nav__disclosure">
        <CollapsibleTrigger asChild>
          <SidebarMenuButton isActive={active} className="sidebar-nav__item" title={entry.label}>
            <span className="sidebar-nav__icon" aria-hidden><Icon size={16} strokeWidth={1.75} /></span>
            <span className="sidebar-nav__label">{entry.label}</span>
            <ChevronRight className="sidebar-nav__caret" size={12} strokeWidth={1.75} aria-hidden />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="sidebar-nav__sub">
            {pages.map((page) => {
              const PageIcon = page.icon;
              const current = route.currentHref === page.href;
              return (
                <SidebarMenuSubItem key={page.id}>
                  <SidebarMenuSubButton asChild isActive={current} className="sidebar-nav__sub-item">
                    <ShellLink href={page.href} draggable={false} aria-current={current ? 'page' : undefined} title={page.label}>
                      {PageIcon ? <span className="sidebar-nav__icon" aria-hidden><PageIcon size={16} strokeWidth={1.75} /></span> : null}
                      <span className="sidebar-nav__label">{page.label}</span>
                    </ShellLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  /** The reader's pinned conversations, listed under the Chat row, which itself stays the link to
   *  /chat. Each row carries the conversation list's own state glyph and opens through
   *  `openBrainSession` with the listing's own capability and title, as the conversation switcher does,
   *  which lands on the full Chat page; a bare controller switch would leave the reader on the page they
   *  were on. The foot of the list opens the conversation switcher. Nothing is drawn until there is a
   *  conversation to show, so a first load and an account that never chatted both leave the Chat row alone.
   *
   *  The sheet closes itself on arrival at a new route, but opening a conversation from /chat or opening
   *  the switcher is not a route change, so both close it explicitly. */
  const pinnedList = (entry: NavEntry) => {
    if (pinnedConversations.length === 0) return null;
    const onChat = entryIsActive(entry, pathname);
    const closeSheet = () => { if (drawer) onDrawerClose?.(); };
    const unreadName = unreadConversations ? `${t.chat.openHistory} (${unreadConversations})` : t.chat.openHistory;
    return (
      <SidebarMenuSub className="sidebar-nav__sub" aria-label={t.nav.pinnedConversations}>
        {pinnedConversations.map((session, slot) => {
          const current = onChat && session.id === activeSessionId;
          const title = session.title || t.brainChat.untitled;
          return (
            <ContextMenuTrigger key={session.id} onOpen={(point) => openPinMenu(point, session)}>
            <SidebarMenuSubItem {...dragReorder.entryProps(session, 'pins', slot)}
              className="sidebar-nav__conversation" data-pin-id={session.id}>
              <ConversationIconPicker session={session} className="sidebar-nav__conversation-icon" />
              <SidebarMenuSubButton isActive={current} className="sidebar-nav__sub-item"
                aria-current={current ? 'true' : undefined}
                title={title}
                onClick={() => { closeSheet(); openBrainSession(session.id, session.capabilities.continue, { title: session.title }); }}>
                <span className="sidebar-nav__label">{title}</span>
              </SidebarMenuSubButton>
              <Button variant="ghost" size="icon" className="sidebar-nav__pin-menu overlay-touch-target"
                aria-label={t.common.actions}
                onClick={event => {
                  const box = event.currentTarget.getBoundingClientRect();
                  openPinMenu({ x: box.left, y: box.bottom }, session);
                }}
                icon={MoreHorizontal} />
            </SidebarMenuSubItem>
            </ContextMenuTrigger>
          );
        })}
        <SidebarMenuSubItem>
          <SidebarMenuSubButton size="sm" className="sidebar-nav__sub-item sidebar-nav__sub-more"
            aria-label={unreadName} onClick={() => { closeSheet(); openHistory(); }}>
            {/* The count is its own non-shrinking badge, so a long label ellipsizes alone; the row's name
                carries the count once and the badge stays out of the accessibility tree. */}
            <span className="sidebar-nav__icon" aria-hidden><Library size={14} strokeWidth={1.75} /></span>
            <span className="sidebar-nav__label">{t.chat.openHistory}</span>
            {unreadConversations ? <SidebarMenuBadge aria-hidden className="sidebar-nav__badge">{unreadConversations}</SidebarMenuBadge> : null}
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      </SidebarMenuSub>
    );
  };

  /** The Chat row with its pinned conversations. Unlike a sub-menu's parent, the row itself stays the link
   *  to /chat, so the list is disclosed by a separate chevron laid over the row's trailing end: the link
   *  never toggles and the chevron never navigates. Whether it is open is the same per-account fold every
   *  sub-menu keeps (`useOpenSubMenus`), keyed by the entry id; an account that never chose sees it
   *  folded, like every other disclosure in the column. With no conversation to show there is nothing to
   *  disclose and no chevron. The wrapper stays either way, so the link is not remounted (and does not
   *  drop focus) when the list arrives. */
  const chatRow = (entry: NavEntry) => {
    const list = pinnedList(entry);
    return (
      <Collapsible open={list !== null && subMenus.isOpen('chat')} onOpenChange={() => subMenus.toggle('chat')} className="sidebar-nav__disclosure">
        {destination(entry)}
        {list ? (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction
                className="sidebar-nav__action"
                aria-label={t.nav.pinnedConversations}
                title={t.nav.pinnedConversations}
              >
                <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>{list}</CollapsibleContent>
          </>
        ) : null}
      </Collapsible>
    );
  };

  /** What a row IS: a destination, or a disclosure over the pages of one. The icon column has no room for
   *  a disclosure, so a sub-menu there collapses to its parent destination and every page stays one click
   *  away through it; the Chat row's pinned conversations are withheld there for the same reason. */
  const rowBody = (entry: NavEntry) => {
    const pages = subMenuPages(entry);
    if (pages && !compact) return subMenu(entry, pages);
    if (entry.id === 'chat' && !compact) return chatRow(entry);
    return destination(entry);
  };

  const row = (entry: NavEntry, region: SidebarGroupId, slot: number) => (
    <Fragment key={entry.id ?? entry.label}>{entryShell(entry, region, slot, rowBody(entry))}</Fragment>
  );

  const groupLabel = (id: SidebarGroupId): string | null => {
    if (id === 'work') return t.nav.sectionWork;
    if (id === 'instance') return t.nav.sectionInstance;
    // The first block is where the reader lands. A header over it names nothing its two rows do not.
    return null;
  };

  // `SidebarProvider` is CONTROLLED by the shell's own fold state rather than owning one: the shell
  // measures the workspace and decides between the full column, the icon rail and the sheet, and a
  // second source of truth for "is it folded" is how the rail and the toggle end up disagreeing.
  //
  // `Sidebar asChild` hands the state attributes to the <nav> the stylesheet already owns, so the
  // primitive contributes its contract (`data-state`, `data-collapsible`, `data-side`) without
  // contributing a second layout. Every part below reads that context.
  const navigation = (
    // Anywhere on the surface that is not a destination opens the editor — that is how a hidden entry is
    // found again. The rows' own triggers are inner ones and win on their rows.
    <ContextMenuTrigger onOpen={customization.openSurfaceMenu}>
    <Sidebar asChild side={side} collapsible={drawer ? 'none' : 'icon'}>
      <nav
        ref={resize.navRef}
        className={`sidebar-nav${drawer ? ' overlay-layer-nav-drawer overlay-nav-drawer' : ''}`}
        data-testid="sidebar-navigation"
        // The stable hook a SKIN targets this column by. `data-testid` is a test handle and `sidebar-nav`
        // is one design's own class, so neither is something a third-party skin should select on; this
        // attribute names the ROLE the element plays in the shell and survives a restyle of either.
        data-shell="sidebar"
        data-mode={mode}
        data-measured={measured}
        data-side={side}
        data-open={drawer && drawerOpen ? true : undefined}
        data-ready={layoutReady || undefined}
        // While a row is being carried the stylesheet switches the entrance animation off for every row:
        // its `both` fill keeps the keyframes' own transform and opacity applied, and an animation beats
        // an inline style, so without this the carried row and the rows making room never visibly move.
        {...dragReorder.surfaceProps}
        // As a sheet this is a layer over the page that takes focus and traps Escape, so it says so.
        // `aria-modal` is claimed only while it is actually open — a closed sheet is inert chrome.
        role={drawer ? 'dialog' : undefined}
        aria-modal={drawer && drawerOpen ? true : undefined}
        aria-label={t.common.primaryNav}
        aria-hidden={hidden ? true : undefined}
        inert={hidden ? true : undefined}
      >
        {/* A dialog needs a way out that is not "guess that the strip of backdrop is a target". First in
            the DOM so Radix's initial focus lands on it. */}
        {drawer ? (
          <Button variant="ghost" size="icon" className="sidebar-nav__close overlay-touch-target"
            onClick={() => {
              const target = drawerReturnFocusRef.current;
              onDrawerClose?.();
              queueMicrotask(() => {
                if (target?.isConnected && !target.inert && !target.closest('[inert]')) target.focus({ preventScroll: true });
              });
            }}
            aria-label={t.common.close}
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </Button>
        ) : null}

        {/* The header is the mark and the instance's name as a quiet wordmark, nothing else (owner, 28 Sep
            2026). It is the control that opens the instance menu, whose label carries the build. Folded
            to the rail the word has no room, so the stylesheet keeps the mark alone. */}
        <SidebarHeader className="sidebar-nav__header">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="sidebar-nav__switcher"
                aria-label={t.nav.instanceMenu}
                title={health.data?.version ? `Chetty ${health.data.version}` : 'Chetty'}
              >
                <img className="sidebar-nav__mark" src={'/icon.png'} alt="" width={28} height={28} />
                <span className="sidebar-nav__wordmark" aria-hidden>{'Chetty'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="sidebar-nav__switcher-title">
                {'Chetty'}
                {health.data?.version ? <span className="sidebar-nav__version">{`v${productLine(health.data.version)}`}</span> : null}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <ShellLink href="/account"><UserRound size={15} strokeWidth={1.75} aria-hidden />{t.nav.account}</ShellLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <ShellLink href="/settings"><Settings2 size={15} strokeWidth={1.75} aria-hidden />{t.nav.settings}</ShellLink>
              </DropdownMenuItem>
              {/* The top bar hides its sign-out button below 768px, so the menu is the way out on a phone. */}
              <DropdownMenuItem disabled={signingOut} onSelect={signOut}>
                <LogOut size={15} strokeWidth={1.75} aria-hidden />{t.common.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarHeader>

        <SidebarContent className="sidebar-nav__body">
          {groups.map((group) => {
            const label = groupLabel(group.id);
            return (
              <SidebarGroup key={group.id} className="sidebar-nav__group" data-group={group.id}>
                {label ? <SidebarGroupLabel className="sidebar-nav__group-label" aria-hidden={compact || undefined}>{label}</SidebarGroupLabel> : null}
                <SidebarGroupContent>
                  <SidebarMenu className="sidebar-nav__menu">
                    {group.entries.map((entry, slot) => row(entry, group.id, slot))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
        </SidebarContent>

        <SidebarFooter className="sidebar-nav__footer">
          {/* The light/dark control on a phone. The interface's brightness is a SKIN here
              (`studio-light` / `studio-oled`, see lib/skins.ts) rather than a CSS theme, so the canonical
              control is the same `SkinSwitcher` the TopBar mounts — not a second switch with its own
              state. Sheet only: on a desktop column the TopBar already carries it. */}
          {drawer ? <SkinSwitcher placement="drawer" /> : null}
          {/* The palette's own affordance, at the foot of the column beside its menu. It is the SAME event
              the TopBar's glyph and the ⌘K binding dispatch, so there is one palette and one way in — this
              is a second door, not a second search. The rail has no room for it and withholds it.
              Its height is the shared default step (36px, 44px on a coarse pointer) taken through the shared
              `Button`, because it is a control in a toolbar row like the menu beside it; the field's fill,
              hairline and radius stay in the column's stylesheet. */}
          <Button
            variant="ghost"
            className="sidebar-nav__search-field justify-start"
            onClick={() => window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT))}
            aria-label={t.common.openCommandPalette}
            title={`${t.common.openCommandPalette} · ${paletteHint}`}
            aria-keyshortcuts="Control+K Meta+K"
          >
            <Search size={16} strokeWidth={1.75} aria-hidden />
            <span className="sidebar-nav__search-label">{t.common.searchSite}</span>
            <kbd className="sidebar-nav__kbd" aria-hidden>{paletteHint}</kbd>
          </Button>
          {/* The fold is the TopBar's control and the column's rail, not a third button down here: one
              affordance for one action. Ctrl/⌘ + \ still folds the column from anywhere. */}
          {/* The keyboard's way into the menu the right-click opens. It has to exist independently of the
              entries: hide them all and there is nothing left to open a menu ON. Same shared default step
              as the field beside it — one row, one control height. */}
          <Button
            variant="ghost"
            size="icon"
            className="sidebar-nav__more"
            aria-label={t.nav.showHidden}
            title={t.nav.showHidden}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              customization.openSurfaceMenu({ x: box.left + box.width / 2, y: box.top });
            }}
          >
            <MoreHorizontal size={16} strokeWidth={1.75} aria-hidden />
          </Button>
        </SidebarFooter>
        {/* As a sheet this element IS the dialog surface (`DialogContent asChild`), which has nowhere to add
            the toast outlet itself: the dock must move in here to stay inside the sheet's focus scope. */}
        {drawer && drawerOpen ? <ToastSurface /> : null}
        {resize.enabled ? <ResizeHandle orientation="vertical" label={t.nav.resizeNavigation}
          value={resize.range.value} min={resize.range.min} max={resize.range.max}
          onDelta={resize.onDelta} onEnd={resize.onEnd} onReset={resize.onReset}
          className="sidebar-nav__resize" /> : onToggleCollapse
          ? <SidebarRail className="sidebar-nav__rail" aria-label={t.common.toggleSidebar} title={t.common.toggleSidebar} /> : null}
        {/* Shadcn menus are intentionally not portalled. In sheet mode they must remain descendants of
            Radix Content so its FocusScope and DismissableLayer treat them as part of the active dialog. */}
        {drawer ? customization.overlays : null}
        {drawer ? pinDeleteDialog : null}
      </nav>
    </Sidebar>
    </ContextMenuTrigger>
  );

  // The provider wraps BOTH branches rather than the column, and that placement is load-bearing: as a
  // sheet the column is handed to `DialogContent asChild`, which needs a single element it can take a
  // ref on. `Sidebar asChild` renders exactly the <nav>; a provider inside would have put a wrapper div
  // between Radix and the surface it is trapping focus in.
  return (
    <SidebarProvider open={!compact} onOpenChange={() => onToggleCollapse?.()}>
      {drawer && drawerOpen ? (
        <SidebarNavSheet label={t.common.primaryNav} onClose={onDrawerClose} returnFocusTo={drawerReturnFocusRef.current}>
          {navigation}
        </SidebarNavSheet>
      ) : navigation}
      {drawer ? null : customization.overlays}
      {drawer ? null : pinDeleteDialog}
    </SidebarProvider>
  );
}
